import { Icons, useToggle } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useRef, useEffect, useState, useCallback } from 'react';

type TabId = 'chat' | 'documents' | 'pinned';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

const TABS: Tab[] = [
  { id: 'chat', label: 'Chat', icon: Icons.MessageCircle01 },
  { id: 'documents', label: 'Files', icon: Icons.Folder },
  { id: 'pinned', label: 'Pinned', icon: Icons.Pin02 },
];

const ChatRoomTabList = ({
  isSearchActive,
  searchQuery,
  setSearchQuery,
  activeTab,
  onTabChange,
}: {
  isSearchActive: ReturnType<typeof useToggle>;
  searchQuery: string | null;
  setSearchQuery: (query: string) => void;
  activeTab: string;
  onTabChange?: (tabId: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    const activeEl = tabRefs.current[activeTab];
    const container = tabsContainerRef.current;
    if (activeEl && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();
      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  useEffect(() => {
    if (isSearchActive.value) {
      inputRef.current?.focus();
    }
  }, [isSearchActive.value]);

  /* ─── Search open ──────────────────────────────────────────── */
  if (isSearchActive.value) {
    return (
      <div className="bg-bg-primary px-xl pt-lg flex">
        <div className="flex-1">
          <div className="bg-bg-light border border-border-secondary rounded-md flex items-center px-lg py-md gap-md w-full">
            <Icons.SearchSm className="size-4 text-text-placeholder shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery ?? ''}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent font-mulish text-sm font-normal leading-5 text-text-primary placeholder:text-text-placeholder outline-none border-none shadow-none ring-0 focus:outline-none focus:ring-0 focus:border-none p-0 min-w-0"
            />
            <button
              type="button"
              onClick={() => {
                isSearchActive.off();
                setSearchQuery(null);
              }}
              className="flex items-center justify-center size-5 rounded-xs p-xxs shrink-0 hover:bg-bg-secondary-hover"
              aria-label="Close search"
            >
              <Icons.XClose className="size-3 text-fg-quaternary" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Tabs closed ──────────────────────────────────────────── */
  return (
    <div className="bg-bg-primary border-b border-border-secondary flex gap-lg items-start px-xl pt-lg">
      {/* Tabs */}
      <div className="flex-1 flex flex-col">
        <div ref={tabsContainerRef} className="flex gap-lg items-start relative">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                type="button"
                onClick={() => onTabChange?.(tab.id)}
                className="flex items-center justify-center pb-md"
              >
                <div className="flex gap-xs items-center px-sm py-xs">
                  <tab.icon
                    className={cn(
                      'size-4 transition-colors duration-200',
                      isActive ? 'text-text-primary' : 'text-text-tertiary'
                    )}
                  />
                  <span
                    className={cn(
                      'font-mulish text-xs font-medium leading-[15px] whitespace-nowrap transition-colors duration-200',
                      isActive ? 'text-text-primary' : 'text-text-tertiary'
                    )}
                  >
                    {tab.label}
                  </span>
                </div>
              </button>
            );
          })}
          {/* Sliding indicator */}
          <div
            className="absolute bottom-0 h-[2px] bg-fg-brand transition-all duration-200 ease-out"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
            }}
          />
        </div>
      </div>

      {/* Search icon — only on chat tab */}
      {activeTab === 'chat' && (
        <button
          type="button"
          onClick={() => {
            isSearchActive.on();
            setSearchQuery(null);
          }}
          className="flex items-center justify-center px-xs py-sm rounded-sm shrink-0"
          aria-label="Search messages"
        >
          <Icons.SearchMd className="size-4 text-fg-quaternary" />
        </button>
      )}
    </div>
  );
};

export default ChatRoomTabList;
